'use client';

type NextImageLoaderProps = {
  src: string;
  width: number;
  quality?: number;
};

// available image formats in the Crystallize CDN
// these width values are derived from their width
// options detected checking the "assets" section
// using the concurrent CRYSTALLIZE UI.
const imageCdnWidthOptions = [100, 200, 500, 768, 1024, 1366, 1600, 1920, 3200];
const imageWidthRegex = /@(\d+)/;

const identifyLargestWebpImageWidth = (imagePath: string): string | null => {
  const matchOfImageResolution = imagePath.match(imageWidthRegex);
  return matchOfImageResolution ? matchOfImageResolution[1] : null;
};

export default function customNextImageLoader({
  src,
  width: imageContainerWidth, // this is what NextJS Image component passes to that loader, see the docs for more info
}: NextImageLoaderProps): string {
  const largestWebpImageWidth = identifyLargestWebpImageWidth(src);

  // (runtime) NOTE:
  // at this point of the runtime, we could already identify whether
  // the incoming src is a webp image or sth else (like the also
  // important/handled SVGs)
  // SVGs will be filtered out as part of the below executed if statement
  // and are - unmodified - sent back to the usage of next image as the
  // very last execution step of this loader.

  if (
    largestWebpImageWidth !== null &&
    imageContainerWidth <= parseInt(largestWebpImageWidth as string)
  ) {
    // the first iteration of the below used find method will return the closest/largest
    // width to - in the final step of this block - define the most performant image
    // for the exposed source set generated by next/image
    const bestResolutionedImageWidth = imageCdnWidthOptions.find(
      (cdnWidthOption) => cdnWidthOption >= imageContainerWidth,
    );

    const bestResolutionedImagePath = src.replace(
      imageWidthRegex,
      `@${bestResolutionedImageWidth}`,
    );

    return bestResolutionedImagePath;
  }

  // in case this loader couldn't handle the incoming src next image passed to the loader
  // or in case the loader couldn't identify a proper path match within the available
  // options in the Crystallize CDN, simply proxy - unmodified - the incoming source back
  // to the usage of next image (like for SVGs as such).
  return src;
}
